/*
 *==================================================
 * Licensed Materials - ADIACENT
 *
 * CR022 Autodesk Vault - CAD Download Basket
 * simple mini basket for MRO_Engineers (replaces the standard MiniCart)
 *
 *==================================================
 */
//Standard libraries
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
//Custom libraries
import { CAD_BASKET } from "../../constants/routes";
//Redux
import { cadBasketItemsSelector, cadBasketNumItemsSelector } from "../../redux/selectors/cadBasket";
//UI
import {
  StyledAccountPopper,
  StyledButton,
  StyledTypography,
  StyledHeaderActions,
  StyledPaper,
} from "@hcl-commerce-store-sdk/react-component";
import ShoppingBasketIcon from "@material-ui/icons/ShoppingBasket";
import { ClickAwayListener } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import styled from "styled-components";

interface MiniCadBasketProps {
  id: string;
  open: boolean;
  handleClick: Function;
  handleClose: Function;
}

const CustomStyledAccountPopper = styled(StyledAccountPopper)`
        &.mini-cad-basket-popper {
          &[x-placement*="bottom"] {
            position: relative;
            background: #fff;
            border: 1px solid #000;
            border-radius: 6px;
          }
          & > .MuiPaper-root {
            width: 350px;
          }
        }
`;

/**
 * MiniCadBasket component
 * displays the number of materials in the CAD Download Basket
 * and routes to the full CAD Basket page.
 * Controlled by the Header (like MiniCart), so it can be opened
 * when a material is added from the PDP.
 */
const MiniCadBasket = React.forwardRef<HTMLButtonElement | null, MiniCadBasketProps>(
  (props: any, ref: any) => {
    const { id, open, handleClick, handleClose } = props;
    const { t } = useTranslation();
    const history = useHistory();
    const numItems = useSelector(cadBasketNumItemsSelector);
    const items = useSelector(cadBasketItemsSelector);
    const [arrowRef, setArrowRef] = useState<any>(null);

    const handleViewFullBasket = () => {
      handleClose();
      history.push(CAD_BASKET);
    };

    return (
      <>
        <StyledButton
          data-testid="header-mini-cad-basket-button"
          ref={ref}
          className="header-actionsButton"
          variant="text"
          color="secondary"
          onClick={handleClick}>
          <StyledHeaderActions>
            <ShoppingBasketIcon />
            <StyledTypography variant="body1" component="p">
              {t("CadBasket.MiniItems", { count: numItems })}
            </StyledTypography>
          </StyledHeaderActions>
        </StyledButton>

        <CustomStyledAccountPopper
          id={id}
          open={open}
          anchorEl={ref?.current}
          onClose={handleClose}
          placement="bottom-end"
          modifiers={{
            flip: {
              enabled: false,
            },
            preventOverflow: {
              enabled: false,
              boundariesElement: "scrollParent",
            },
            hide: {
              enabled: false,
            },
            arrow: {
              enabled: true,
              element: arrowRef,
            },
          }}
          className="mini-cad-basket-popper">
          <span className="arrow" ref={setArrowRef} />
          <ClickAwayListener onClickAway={() => handleClose()}>
            <StyledPaper className="vertical-padding-1 horizontal-padding-1">
              <StyledTypography variant="h6" className="bottom-margin-1">
                {t("CadBasket.Title")}
              </StyledTypography>
              {numItems > 0 ? (
                <>
                  <StyledTypography variant="body1" className="bottom-margin-1">
                    {t("CadBasket.MiniItems", { count: numItems })}
                  </StyledTypography>
                  <TableContainer className="bottom-margin-2" style={{ maxHeight: "300px" }}>
                    <Table size="small" stickyHeader aria-label="mini cad basket table">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>{t("CadBasket.ManufacturerPartNumber")}</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>{t("CadBasket.CadAttachments")}</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item: any) => (
                          <TableRow key={item.itemId}>
                            <TableCell>
                              {item.seo ? (
                                /** routes to the Product Page, like the MiniCart items */
                                <Link to={item.seo} onClick={() => handleClose()}>
                                  {item.materialMnfPartNum}
                                </Link>
                              ) : (
                                item.materialMnfPartNum
                              )}
                            </TableCell>
                            {/* only the CAD files included in the ZIP request */}
                            <TableCell align="center">
                              {(item.assets ?? []).filter((a: any) => !a.excluded).length}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <StyledTypography variant="body1" className="bottom-margin-2">
                  {t("CadBasket.Empty")}
                </StyledTypography>
              )}
              <StyledButton
                data-testid="mini-cad-basket-view-full"
                color="primary"
                fullWidth
                onClick={handleViewFullBasket}>
                {t("CadBasket.ViewFullBasket")}
              </StyledButton>
            </StyledPaper>
          </ClickAwayListener>
        </CustomStyledAccountPopper>
      </>
    );
  }
);

export default MiniCadBasket;
